import { FieldSet, Records } from "airtable"
import { GetStaticPropsContext, NextPage } from "next"
import { NextSeo } from "next-seo"
import Chronology from "../../components/chronology"
import FeaturesIndex from "../../components/featuresIndex"
import PageLayout, { PageLayoutProps } from "../../components/pageLayout"
import Resources from "../../components/resources"
import { getAllPages } from "../../util/airtable-pages"
import { getAllPagesContent } from "../../util/contentful-api"
import { base, minifyRecord, minifyRecords } from "../api/utils/airtable"
import { getPageData } from "../api/[parent]"
import { ResourcesRoutes, Routes } from "./[id]"

export interface ContentDataProps {
  contentData: Records<FieldSet>
}

export interface PageFields {
  pageData: PageLayoutProps
  contentData?: Records<FieldSet>
  recommededData?: Records<FieldSet>
  featuresData?: any
  parentData?: FieldSet
}

const Page: NextPage<PageFields> = (props) => {
  const { contentData, parentData, recommededData, pageData, featuresData } = props

  const pageSlug = pageData.id
  let pageTitle = pageData.title ? pageData.title.toString() : ""

  const Layout = () => {
    switch (pageSlug) {
      case Routes.Artist:
        pageTitle = "The Artist"
        return (
          <>
            <NextSeo title={pageTitle} />
            <PageLayout {...pageData}>{contentData && <Chronology contentData={contentData} />}</PageLayout>
          </>
        )
      case Routes.Resources:
        return (
          <>
            <NextSeo title={pageTitle} />
            <PageLayout {...pageData} parentData={parentData}>
              {contentData && <Resources contentData={contentData} recommededData={recommededData} />}
            </PageLayout>
          </>
        )
      case Routes.Features:
        return (
          <>
            <NextSeo title={pageTitle} />
            <PageLayout {...pageData} parentData={parentData}>
              <FeaturesIndex featuresData={featuresData} />
            </PageLayout>
          </>
        )
      default:
        return (
          <>
            <NextSeo title={pageTitle} />
            <PageLayout {...pageData} />
          </>
        )
    }
  }

  return (
    <>
      <NextSeo title={pageTitle} />
      <Layout />
    </>
  )
}

export default Page

export async function getStaticProps(context: GetStaticPropsContext) {
  try {
    // get the parent slug
    const pageSlug = context.params ? context.params.parent : ""

    if (!pageSlug) {
      return { notFound: true }
    }

    // get the parent page data
    const pageData = await getPageData(pageSlug.toString())

    if (!pageData || !pageSlug || (Object.values(ResourcesRoutes) as string[]).includes(pageSlug.toString())) {
      return { notFound: true }
    }

    let contentData: Records<FieldSet> | undefined
    if (pageSlug == Routes.Artist) {
      const artisttable = base("Chronology")
      const artistview = "All cards"
      contentData = await artisttable.select({ view: artistview }).all()
    }

    let featuresData: any
    if (pageSlug == Routes.Features) {
      featuresData = await getAllPagesContent()
    }

    let recommededData: Records<FieldSet> | undefined
    if (pageSlug == Routes.Resources) {
      const allpagestable = base("All Pages")
      const recommendedtable = base("Recommended")
      const recommendedview = "Published"
      const resources = "Published Resources"
      contentData = await allpagestable.select({ view: resources }).all()
      recommededData = await recommendedtable.select({ view: recommendedview }).all()
    }

    return {
      props: {
        pageData: minifyRecord(pageData).fields,
        contentData: contentData ? minifyRecords(contentData) : null,
        featuresData: featuresData ? featuresData : null,
        recommededData: recommededData ? minifyRecords(recommededData) : null,
      },
    }
  } catch (err) {
    console.error(err)
  }
}

export const getStaticPaths = async () => {
  const pages = await getAllPages()

  const paths = pages.map((page) => ({
    params: { parent: page.params.id },
  }))

  return {
    paths,
    fallback: false,
  }
}
